import { useForm } from '@mantine/form';

import { prophetTypes } from '../types';

import { isValidObjectId } from '@/utils';

/** NumberInput yields '' (or a string) for empty/unfinished input */
export type LimitFormValue = number | string;

export type FormValues = {
  appPage: prophetTypes.Page['appPage'];
  funnels: prophetTypes.Page['funnels'];
  aiId: number | undefined;
  isNightModeEnabled: boolean;
  limits: {
    perMinute: LimitFormValue;
    perHour: LimitFormValue;
    perDay: LimitFormValue;
  };
};

/** Empty field → null: the server clears the value (system default / no limit) */
export const toApiLimit = (value: LimitFormValue): number | null => {
  return typeof value === 'number' ? value : null;
};

const validateLimitValue = (value: LimitFormValue) => {
  if (value === '') {
    return null;
  }
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
    return 'Целое число не меньше 1';
  }
  return null;
};

export const useProphetPageForm = ({ initialValues }: { initialValues?: FormValues }) => {
  return useForm<FormValues>({
    initialValues: {
      appPage: initialValues?.appPage || {
        _id: '',
      },
      funnels: {
        completedNewDialogWithRefuse: initialValues?.funnels.completedNewDialogWithRefuse || [],
        completedNewDialog: initialValues?.funnels.completedNewDialog || [],
        manager: initialValues?.funnels.manager || [],
        underage: initialValues?.funnels.underage || [],
      },
      aiId: initialValues?.aiId,
      isNightModeEnabled: initialValues?.isNightModeEnabled ?? true,
      limits: {
        perMinute: initialValues?.limits.perMinute ?? '',
        perHour: initialValues?.limits.perHour ?? '',
        perDay: initialValues?.limits.perDay ?? '',
      },
    },

    validate: {
      appPage: (v) => {
        if (!v || !v._id || !isValidObjectId(v._id)) {
          return 'Не верный ID страницы';
        }
      },
      funnels: (v) => {
        if (!v.manager || !v.manager.length || !v.manager.every((i) => isValidObjectId(i._id))) {
          return 'Не верный ID воронки';
        }
        if (
          !v.completedNewDialog ||
          !v.completedNewDialog.length ||
          !v.completedNewDialog.every((i) => isValidObjectId(i._id))
        ) {
          return 'Не верный ID воронки';
        }
        if (v.completedNewDialogWithRefuse) {
          if (!v.completedNewDialogWithRefuse.every((i) => isValidObjectId(i._id))) {
            return 'Не верный ID воронки';
          }
        }
        if (v.underage) {
          if (!v.underage.every((i) => isValidObjectId(i._id))) {
            return 'Не верный ID воронки';
          }
        }
      },
      aiId: (v) => {
        // Empty field = clear. Otherwise mirror the server rule (integer >= 1):
        // it rejects 0 / negatives / fractions with a 400, so catching them
        // here keeps the error inline on the field instead of in a toast.
        if (v === undefined) {
          return null;
        }
        if (!Number.isInteger(v) || v < 1) {
          return 'Целое число не меньше 1';
        }
        return null;
      },
      limits: {
        perMinute: (v) => validateLimitValue(v),
        perHour: (v, values) => {
          const error = validateLimitValue(v);
          if (error) {
            return error;
          }
          const { perMinute } = values.limits;
          if (typeof v === 'number' && typeof perMinute === 'number' && v < perMinute) {
            return 'Часовой лимит не может быть меньше минутного';
          }
          return null;
        },
        perDay: (v, values) => {
          const error = validateLimitValue(v);
          if (error) {
            return error;
          }
          // The server validates all three pairs, including perDay >= perMinute,
          // so with an empty hourly limit the minute one is still the floor —
          // otherwise the form lets through a payload the server answers with a
          // 400 whose message names a pair the user never filled in.
          const { perMinute, perHour } = values.limits;
          const floor = typeof perHour === 'number' ? perHour : perMinute;
          if (typeof v === 'number' && typeof floor === 'number' && v < floor) {
            return floor === perHour
              ? 'Суточный лимит не может быть меньше часового'
              : 'Суточный лимит не может быть меньше минутного';
          }
          return null;
        },
      },
    },
  });
};
